import { User, Dumbbell, Apple, MessageCircle, type LucideIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useHaptics } from '@/hooks/useHaptics';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { useProfile } from '@/hooks/useUserQueries';
import { useTheme } from '@/contexts/ThemeContext';

// NOTE: if these tabs/routes change, update supabase/functions/_shared/appNavigation.ts
// (the AI coach's navigation knowledge) — there are no shared route constants.
const ACTIVE_WORKOUT_KEY = 'active_workout';

const pageImports: Record<string, () => Promise<unknown>> = {
  '/':        () => import('@/pages/Profile'),
  '/workout': () => import('@/pages/WorkoutTracker'),
  '/food':    () => import('@/pages/FoodTracker'),
  '/coach':   () => import('@/pages/Coach'),
};

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
}

// Order per spec: Profile · Train · Food · Coach.
const navItems: NavItem[] = [
  { to: '/',        icon: User,          label: 'Profile' },
  { to: '/workout', icon: Dumbbell,      label: 'Train' },
  { to: '/food',    icon: Apple,         label: 'Food' },
  { to: '/coach',   icon: MessageCircle, label: 'Coach' },
];

function BottomNavComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lightTap } = useHaptics();
  const { isDark } = useTheme();
  const [hasActiveWorkout, setHasActiveWorkout] = useState(false);
  const { data: profile } = useProfile();
  const unreadCoach = profile?.unread_coach_messages ?? 0;
  const coachActive = location.pathname.startsWith('/coach');

  const activeIndex = navItems.findIndex(
    (item) =>
      location.pathname === item.to ||
      (item.to !== '/' && location.pathname.startsWith(item.to)),
  );

  useEffect(() => {
    const check = () => {
      const saved = localStorage.getItem(ACTIVE_WORKOUT_KEY);
      if (!saved) return setHasActiveWorkout(false);
      try {
        const parsed = JSON.parse(saved);
        setHasActiveWorkout(!!parsed?.startedAt);
      } catch {
        setHasActiveWorkout(false);
      }
    };
    check();
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener('storage', check);
    window.addEventListener('active-workout-changed', check);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('active-workout-changed', check);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [location.pathname]);

  const prefetch = useCallback((path: string) => {
    const fn = pageImports[path];
    if (fn) fn();
  }, []);

  const inactiveColor = 'var(--pc-ink-3)';

  return (
    <div
      data-bottom-nav
      className="fixed left-0 right-0 z-50 flex items-center pointer-events-none"
      style={{
        bottom: 0,
        padding: `0 18px max(env(safe-area-inset-bottom, 0px), 12px)`,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--scrim-base)',
        }}
      />
      <NavPill
        navItems={navItems}
        activeIndex={activeIndex}
        hasActiveWorkout={hasActiveWorkout}
        unreadCoach={unreadCoach}
        coachActive={coachActive}
        isDark={isDark}
        inactiveColor={inactiveColor as string}
        onSelect={(to) => { lightTap(); navigate(to); }}
        onPrefetch={prefetch}
      />
    </div>
  );
}

export const BottomNav = memo(BottomNavComponent);

interface NavPillProps {
  navItems: NavItem[];
  activeIndex: number;
  hasActiveWorkout: boolean;
  unreadCoach: number;
  coachActive: boolean;
  isDark: boolean;
  inactiveColor: string;
  onSelect: (to: string) => void;
  onPrefetch: (to: string) => void;
}

function NavPill({
  navItems,
  activeIndex,
  hasActiveWorkout,
  unreadCoach,
  coachActive,
  isDark,
  inactiveColor,
  onSelect,
  onPrefetch,
}: NavPillProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const PILL_W = 58;
  const PILL_H = 44;
  const [blob, setBlob] = useState<{ left: number; top: number; width: number; height: number }>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  const measure = useCallback(() => {
    const idx = activeIndex >= 0 ? activeIndex : 0;
    const btn = btnRefs.current[idx];
    const container = containerRef.current;
    if (!btn || !container) return;
    const b = btn.getBoundingClientRect();
    const c = container.getBoundingClientRect();
    setBlob({
      left: b.left - c.left + (b.width - PILL_W) / 2,
      top: b.top - c.top + (b.height - PILL_H) / 2,
      width: PILL_W,
      height: PILL_H,
    });
  }, [activeIndex]);

  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-auto relative"
      style={{
        flex: 1,
        height: 62,
        borderRadius: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: 3,
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14), 0 6px 18px rgba(0,0,0,0.28)',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 26,
          background: 'var(--scrim-base)',
          zIndex: -1,
        }}
      />
      <div
        aria-hidden
        className="pc-glass"
        style={{
          position: 'absolute',
          left: blob.left,
          top: blob.top,
          width: blob.width,
          height: blob.height,
          borderRadius: 22,
          opacity: blob.width > 0 ? 1 : 0,
          transition:
            'left 200ms cubic-bezier(0,0,0.2,1), top 200ms cubic-bezier(0,0,0.2,1), width 200ms cubic-bezier(0,0,0.2,1), height 200ms cubic-bezier(0,0,0.2,1)',
          pointerEvents: 'none',
          zIndex: 0,
          // Lime-tinted glass highlight (Health-OS). Keeps the .pc-glass blur;
          // overrides only the tint + border + shadow so the capsule reads as
          // lime instead of blue/white.
          background:
            'linear-gradient(155deg, rgba(163,230,53,0.14), rgba(163,230,53,0.06))',
          border: '1px solid rgba(163,230,53,0.22)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.20), 0 4px 14px rgba(0,0,0,0.35)',
        }}
      />
      {navItems.map((item, idx) => {
        const isActive = idx === activeIndex;
        const Icon = item.icon;
        const showDot = item.to === '/workout' && hasActiveWorkout;
        const showCoachBadge = item.to === '/coach' && unreadCoach > 0 && !coachActive;
        return (
          <button
            key={item.to}
            ref={(el) => (btnRefs.current[idx] = el)}
            onClick={() => onSelect(item.to)}
            onPointerEnter={() => onPrefetch(item.to)}
            onFocus={() => onPrefetch(item.to)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: 1,
              height: 56,
              borderRadius: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              position: 'relative',
              cursor: 'pointer',
              padding: 0,
              zIndex: 1,
            }}
          >
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <Icon
                size={23}
                strokeWidth={2.2}
                color={isActive ? '#A3E635' : inactiveColor}
                style={{ transition: 'color 200ms ease' }}
              />
              {showDot && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full"
                  style={{ background: '#A3E635' }}
                >
                  <span
                    className="absolute inset-0 rounded-full animate-ping opacity-75"
                    style={{ background: '#A3E635' }}
                  />
                </motion.span>
              )}
              {showCoachBadge && (
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center shadow-sm"
                  aria-label={`${unreadCoach} new coach message${unreadCoach === 1 ? '' : 's'}`}
                >
                  {unreadCoach > 9 ? '9+' : unreadCoach}
                </motion.span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
